import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { fetchApplications, deleteApplication, fetchApplicationById } from "../../../services/check-api";
import Table from "../../../utils/Table";
import ActionButtons from "../../../utils/ActionButtons";
import { getStatusDot, formatDate } from "../../../utils/TableUtils";
import CheckApplicationModal from "../../../components/Modals/CheckApplicationModal";
import { useAuth } from "../../../contexts/AuthContext"; 
import { useToast } from "../../../contexts/ToastContext";
import { useLoading } from "../../../contexts/LoadingContext";


function Applications() {
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [filterInput, setFilterInput] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const { role } = useAuth();
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const pageSize = 10;

  const handleEdit = (applicationData) => {
    setSelectedApplication(applicationData);
    setModalShow(true);
  };

  const refreshAppData = () => {
    setModalShow(false);
    setSelectedApplication(null);
    if (id) {
      fetchData(id);
    } else {
      fetchData();
    }
    
  }

  const handleDelete = async (rowData) => {
    startLoading();
    try {
      const { id} = rowData;
      if (!id) {
        showToast("Invalid ID, please try again.", "error");
        return;
      }
      const response = await deleteApplication(id);

      if (response && response.message) {
        setData((prevData) =>
          prevData.filter((applicaton) => applicaton.id !== id)
        );
        showToast(response.message, "success");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("An error occurred while deleting the community.", "error");
      }
    } finally {
      stopLoading();
    }
  };

  const handlePageChange = (newPageIndex) => {
    setPageIndex(newPageIndex);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Role",
        accessor: "role",
      },
      {
        Header: "Name",
        accessor: "name",
        sortType: (rowA, rowB) => {
          const a = rowA.values.name?.toLowerCase() || "";
          const b = rowB.values.name?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Address",
        accessor: "address",
        sortType: (rowA, rowB) => {
          const a = rowA.values.address?.toLowerCase() || "";
          const b = rowB.values.address?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Phone Number",
        accessor: "phoneNumber",
        sortType: (rowA, rowB) => {
          const a = rowA.values.phoneNumber?.toLowerCase() || "";
          const b = rowB.values.phoneNumber?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Email",
        accessor: "email",
        sortType: (rowA, rowB) => {
          const a = rowA.values.email?.toLowerCase() || "";
          const b = rowB.values.email?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Community Email Address",
        accessor: "communityEmailAddress",
        sortType: (rowA, rowB) => {
          const a = rowA.values.communityEmailAddress?.toLowerCase() || "";
          const b = rowB.values.communityEmailAddress?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Plan Review Type",
        accessor: "planReviewType",
        sortType: (rowA, rowB) => {
          const a = rowA.values.planReviewType?.toLowerCase() || "";
          const b = rowB.values.planReviewType?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Comments",
        accessor: "comments",
        sortType: (rowA, rowB) => {
          const a = rowA.values.comments?.toLowerCase() || "";
          const b = rowB.values.comments?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => getStatusDot(value),
        sortType: (rowA, rowB) => {
          const a = rowA.values.status?.toLowerCase() || "";
          const b = rowB.values.status?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <ActionButtons
            onEdit={handleEdit} 
            onDelete={handleDelete}
            rowData={row.original}
            showDelete={role === "Admin"}
            additonalBtn={false} 
          />
        ),
      },
    ];
  
    return baseColumns;
  }, [role]);
  
  const fetchData = async (id = null) => {
    setLoading(true);
    startLoading();
    try {
      let response;
      if (id) {
        // Fetch data for a specific application by ID
        response = await fetchApplicationById(id, pageIndex + 1, pageSize);
        setData(response.data);
      } else {
        // Fetch all applications
        response = await fetchApplications(pageIndex + 1, pageSize);
        setData(response.data);
        setPageCount(response.pagination.totalPages);
      }
    } catch (error) {
      
      console.error("Error fetching data:", error?.response?.data?.message);
    } finally {
      setLoading(false);
      stopLoading();
    }
  };

  useEffect(() => {
    if (id) {
      fetchData(id);
    } else {
      fetchData();
    }
  }, [id, pageIndex]);

  return (
    <div className="subMain-content">
      <div>
        <h1 className="mt-4">Plan Review Request</h1>
      </div>
    <Table
      className="custom-simplecheck-table"
      columns={columns}
      data={data}
      filterInput={filterInput}
      setFilterInput={setFilterInput}
      onPageChange={handlePageChange}
      currentPage={pageIndex}
      pageCount={pageCount}
      loading={loading}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
    />
    {selectedApplication && <CheckApplicationModal
        show={modalShow}
        data={selectedApplication}
        onHide={refreshAppData}
      />}
    </div>
  );
}

export default Applications;
